import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getAuthHeaders } from '../utils/authHeaders';

export default function ProjectTimeTracking() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const companyId = localStorage.getItem('selectedCompanyId');
  const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

  const [activeTab, setActiveTab] = useState('entries');
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [activeTimer, setActiveTimer] = useState(null);
  const [timerElapsed, setTimerElapsed] = useState(0);
  const [showManual, setShowManual] = useState(false);
  const [manualForm, setManualForm] = useState({ task_id: '', date: '', hours: '', minutes: '', description: '' });
  const [tasks, setTasks] = useState([]);
  const [costRates, setCostRates] = useState([]);
  const [laborSummary, setLaborSummary] = useState(null);
  const [showCostModal, setShowCostModal] = useState(false);
  const [costForm, setCostForm] = useState({ user_id: '', hourly_rate: '', effective_from: '' });
  const [costEditId, setCostEditId] = useState(null);

  const loadEntries = useCallback(() => {
    if (!companyId || !projectId) return;
    setLoading(true);
    fetch(`${API}/companies/${companyId}/projects/${projectId}/time`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : { entries: [], summary: {} })
      .then(d => {
        setEntries(Array.isArray(d.entries) ? d.entries : (Array.isArray(d) ? d : []));
        setSummary(d.summary || null);
      })
      .catch(() => { setEntries([]); setSummary(null); })
      .finally(() => setLoading(false));
  }, [companyId, projectId, API]);

  const loadActiveTimer = useCallback(() => {
    if (!companyId) return;
    fetch(`${API}/companies/${companyId}/time/active`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(d => setActiveTimer(d && d.id ? d : null))
      .catch(() => setActiveTimer(null));
  }, [companyId, API]);

  const loadTasks = useCallback(() => {
    if (!companyId || !projectId) return;
    fetch(`${API}/companies/${companyId}/projects/${projectId}/tasks`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : []).then(d => setTasks(Array.isArray(d) ? d : [])).catch(() => {});
  }, [companyId, projectId, API]);

  const loadCostRates = useCallback(() => {
    if (!companyId) return;
    fetch(`${API}/companies/${companyId}/time/cost-rates`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : []).then(d => setCostRates(Array.isArray(d) ? d : [])).catch(() => {});
  }, [companyId, API]);

  const loadLaborSummary = useCallback(() => {
    if (!companyId || !projectId) return;
    fetch(`${API}/companies/${companyId}/projects/${projectId}/time/labor-summary`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null).then(d => setLaborSummary(d)).catch(() => {});
  }, [companyId, projectId, API]);

  useEffect(() => { loadEntries(); loadActiveTimer(); loadTasks(); }, [loadEntries, loadActiveTimer, loadTasks]);

  // Timer ticker
  useEffect(() => {
    if (!activeTimer || activeTimer.status !== 'running') { setTimerElapsed(0); return; }
    const start = new Date(activeTimer.start_time).getTime();
    const paused = (activeTimer.paused_duration || 0) * 1000;
    const tick = () => setTimerElapsed(Math.floor((Date.now() - start - paused) / 1000));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [activeTimer]);

  const fmtTime = (secs) => {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
  };

  const startTimer = (taskId) => {
    fetch(`${API}/companies/${companyId}/projects/${projectId}/tasks/${taskId}/timer/start`, {
      method: 'POST', headers: getAuthHeaders()
    }).then(r => r.ok ? r.json() : null).then(() => loadActiveTimer()).catch(() => {});
  };

  const pauseTimer = () => {
    if (!activeTimer) return;
    fetch(`${API}/companies/${companyId}/time/${activeTimer.id}/pause`, { method: 'POST', headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null).then(() => loadActiveTimer()).catch(() => {});
  };

  const stopTimer = () => {
    if (!activeTimer) return;
    fetch(`${API}/companies/${companyId}/time/${activeTimer.id}/stop`, { method: 'POST', headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null).then(() => { loadActiveTimer(); loadEntries(); }).catch(() => {});
  };

  const submitManual = () => {
    const totalMin = (parseInt(manualForm.hours) || 0) * 60 + (parseInt(manualForm.minutes) || 0);
    if (!manualForm.task_id || totalMin <= 0) { window.alert('Task and time required'); return; }
    fetch(`${API}/companies/${companyId}/time/manual`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ task_id: manualForm.task_id, project_id: projectId, date: manualForm.date || undefined, duration_minutes: totalMin, description: manualForm.description })
    }).then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(() => { setShowManual(false); setManualForm({ task_id: '', date: '', hours: '', minutes: '', description: '' }); loadEntries(); })
      .catch(() => window.alert('Failed to add manual entry'));
  };

  const approveEntry = (id, status) => {
    fetch(`${API}/companies/${companyId}/time/${id}/approve`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ status })
    }).then(r => r.ok ? r.json() : null).then(() => loadEntries()).catch(() => {});
  };

  const saveCostRate = () => {
    const method = costEditId ? 'PUT' : 'POST';
    const url = costEditId ? `${API}/companies/${companyId}/time/cost-rates/${costEditId}` : `${API}/companies/${companyId}/time/cost-rates`;
    fetch(url, { method, headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(costForm) })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(() => { setShowCostModal(false); setCostEditId(null); setCostForm({ user_id: '', hourly_rate: '', effective_from: '' }); loadCostRates(); })
      .catch(() => window.alert('Failed to save cost rate'));
  };

  const statusColor = { pending: '#f59e0b', approved: '#22c55e', rejected: '#ef4444' };

  if (!companyId) return <div style={pageStyle}><p>Please select a company.</p></div>;

  return (
    <div style={pageStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div>
          <button onClick={() => navigate(`/company/projects/${projectId}`)} style={linkBtn}>← Back to Project</button>
          <h2 style={{ margin: '4px 0 0', fontSize: '1.4rem', fontWeight: 700 }}>⏱️ Time & Progress</h2>
        </div>
        <button onClick={() => setShowManual(true)} style={primaryBtn}>+ Manual Entry</button>
      </div>

      {/* Active Timer */}
      {activeTimer && (
        <div style={{ ...cardStyle, marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderLeft: '4px solid #3b82f6' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: '1.5rem', fontFamily: 'monospace', color: '#3b82f6' }}>{fmtTime(timerElapsed)}</div>
            <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
              {activeTimer.task?.name || 'Task'} — {activeTimer.status === 'paused' ? '⏸ Paused' : '▶ Running'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {activeTimer.status === 'running' && <button onClick={pauseTimer} style={{ ...actionBtn, backgroundColor: '#f59e0b' }}>⏸ Pause</button>}
            {activeTimer.status === 'paused' && <button onClick={() => {
              fetch(`${API}/companies/${companyId}/projects/${projectId}/tasks/${activeTimer.task_id}/timer/start`, { method: 'POST', headers: getAuthHeaders() })
                .then(() => loadActiveTimer());
            }} style={{ ...actionBtn, backgroundColor: '#22c55e' }}>▶ Resume</button>}
            <button onClick={stopTimer} style={{ ...actionBtn, backgroundColor: '#ef4444' }}>⏹ Stop</button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0', borderBottom: '2px solid #e2e8f0', marginBottom: '16px' }}>
        {['entries','costs','labor'].map(t => (
          <button key={t} onClick={() => { setActiveTab(t); if (t === 'costs') loadCostRates(); if (t === 'labor') loadLaborSummary(); }} style={{
            padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.85rem',
            fontWeight: activeTab === t ? 600 : 400, color: activeTab === t ? '#3b82f6' : '#64748b',
            borderBottom: activeTab === t ? '2px solid #3b82f6' : '2px solid transparent', marginBottom: '-2px', textTransform: 'capitalize'
          }}>{t === 'entries' ? 'Time Entries' : t === 'costs' ? 'Cost Rates' : 'Labor Summary'}</button>
        ))}
      </div>

      {/* Time Entries */}
      {activeTab === 'entries' && (
        <>
          {summary && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '12px', marginBottom: '16px' }}>
              <div style={statCard}><div style={statLabel}>Total Hours</div><div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#3b82f6' }}>{summary.total_hours || '0.00'}</div></div>
              <div style={statCard}><div style={statLabel}>Total Cost</div><div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#22c55e' }}>${summary.total_cost || '0.00'}</div></div>
              <div style={statCard}><div style={statLabel}>Entries</div><div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#8b5cf6' }}>{summary.total_entries || 0}</div></div>
            </div>
          )}
          {initialLoading ? <p>Loading...</p> : (
            <div style={{ overflowX: 'auto' }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Date</th>
                    <th style={thStyle}>Task</th>
                    <th style={thStyle}>User</th>
                    <th style={thStyle}>Duration</th>
                    <th style={thStyle}>Cost</th>
                    <th style={thStyle}>Source</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map(e => (
                    <tr key={e.id}>
                      <td style={tdStyle}>{e.date || (e.start_time ? e.start_time.split('T')[0] : '-')}</td>
                      <td style={tdStyle}>{e.task?.name || '-'}</td>
                      <td style={tdStyle}>{e.user?.name || '-'}</td>
                      <td style={tdStyle}>{e.duration_minutes ? `${Math.floor(e.duration_minutes/60)}h ${e.duration_minutes%60}m` : '-'}</td>
                      <td style={tdStyle}>${e.labor_cost || '0.00'}</td>
                      <td style={tdStyle}><span style={{ fontSize: '0.75rem', color: e.source === 'timer' ? '#3b82f6' : '#6b7280' }}>{e.source || 'manual'}</span></td>
                      <td style={tdStyle}><span style={{ ...badge, backgroundColor: statusColor[e.approval_status] || '#94a3b8' }}>{e.approval_status || 'pending'}</span></td>
                      <td style={tdStyle}>
                        {e.approval_status === 'pending' && (
                          <div style={{ display: 'flex', gap: '4px' }}>
                            <button onClick={() => approveEntry(e.id, 'approved')} style={{ ...smallBtn, color: '#22c55e' }}>✓</button>
                            <button onClick={() => approveEntry(e.id, 'rejected')} style={{ ...smallBtn, color: '#ef4444' }}>✗</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                  {entries.length === 0 && <tr><td colSpan="8" style={{ ...tdStyle, textAlign: 'center', color: '#94a3b8' }}>No time entries</td></tr>}
                </tbody>
              </table>
            </div>
          )}

          {/* Start Timer for a Task */}
          {tasks.length > 0 && (
            <div style={{ ...cardStyle, marginTop: '16px' }}>
              <h4 style={{ margin: '0 0 8px', fontSize: '0.9rem' }}>Quick Start Timer</h4>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {tasks.filter(t => !['completed','cancelled'].includes(t.status)).slice(0, 10).map(t => (
                  <button key={t.id} onClick={() => startTimer(t.id)} style={{ ...smallBtn, display: 'flex', alignItems: 'center', gap: '4px' }}>
                    ▶ {t.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Cost Rates */}
      {activeTab === 'costs' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
            <button onClick={() => { setCostEditId(null); setCostForm({ user_id: '', hourly_rate: '', effective_from: '' }); setShowCostModal(true); }} style={primaryBtn}>+ Add Rate</button>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead><tr><th style={thStyle}>User</th><th style={thStyle}>Hourly Rate</th><th style={thStyle}>Effective From</th><th style={thStyle}>Actions</th></tr></thead>
              <tbody>
                {costRates.map(r => (
                  <tr key={r.id}>
                    <td style={tdStyle}>{r.user?.name || `User #${r.user_id}`}</td>
                    <td style={tdStyle}>${r.hourly_rate}</td>
                    <td style={tdStyle}>{r.effective_from}</td>
                    <td style={tdStyle}>
                      <button onClick={() => { setCostEditId(r.id); setCostForm({ user_id: r.user_id, hourly_rate: r.hourly_rate, effective_from: r.effective_from }); setShowCostModal(true); }} style={smallBtn}>Edit</button>
                    </td>
                  </tr>
                ))}
                {costRates.length === 0 && <tr><td colSpan="4" style={{ ...tdStyle, textAlign: 'center', color: '#94a3b8' }}>No cost rates defined</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Labor Summary */}
      {activeTab === 'labor' && laborSummary && (
        <div style={{ display: 'grid', gap: '16px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px' }}>
            <div style={statCard}><div style={statLabel}>Total Labor Hours</div><div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#3b82f6' }}>{laborSummary.total_hours || '0.00'}</div></div>
            <div style={statCard}><div style={statLabel}>Total Labor Cost</div><div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#22c55e' }}>${laborSummary.total_cost || '0.00'}</div></div>
          </div>
          {Array.isArray(laborSummary.by_user) && laborSummary.by_user.length > 0 && (
            <div style={cardStyle}>
              <h4 style={{ margin: '0 0 12px', fontSize: '0.9rem' }}>By Team Member</h4>
              <table style={tableStyle}>
                <thead><tr><th style={thStyle}>Member</th><th style={thStyle}>Hours</th><th style={thStyle}>Cost</th></tr></thead>
                <tbody>
                  {laborSummary.by_user.map((u, i) => (
                    <tr key={i}><td style={tdStyle}>{u.user?.name || `User #${u.user_id}`}</td><td style={tdStyle}>{u.total_hours}</td><td style={tdStyle}>${u.total_cost}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      {activeTab === 'labor' && !laborSummary && <p style={{ color: '#94a3b8' }}>Loading labor summary...</p>}

      {/* Manual Entry Modal */}
      {showManual && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <h3 style={{ margin: '0 0 16px' }}>Manual Time Entry</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div>
                <label style={labelStyle}>Task *</label>
                <select value={manualForm.task_id} onChange={e => setManualForm({ ...manualForm, task_id: e.target.value })} style={inputStyle}>
                  <option value="">Select Task</option>
                  {tasks.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div><label style={labelStyle}>Date</label><input type="date" value={manualForm.date} onChange={e => setManualForm({ ...manualForm, date: e.target.value })} style={inputStyle} /></div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <div style={{ flex: 1 }}><label style={labelStyle}>Hours</label><input type="number" min="0" value={manualForm.hours} onChange={e => setManualForm({ ...manualForm, hours: e.target.value })} style={inputStyle} /></div>
                <div style={{ flex: 1 }}><label style={labelStyle}>Minutes</label><input type="number" min="0" max="59" value={manualForm.minutes} onChange={e => setManualForm({ ...manualForm, minutes: e.target.value })} style={inputStyle} /></div>
              </div>
              <div><label style={labelStyle}>Description</label><textarea value={manualForm.description} onChange={e => setManualForm({ ...manualForm, description: e.target.value })} style={{ ...inputStyle, minHeight: '50px' }} /></div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
              <button onClick={() => setShowManual(false)} style={cancelBtn}>Cancel</button>
              <button onClick={submitManual} style={primaryBtn}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Cost Rate Modal */}
      {showCostModal && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <h3 style={{ margin: '0 0 16px' }}>{costEditId ? 'Edit Cost Rate' : 'Add Cost Rate'}</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div><label style={labelStyle}>User ID *</label><input type="number" value={costForm.user_id} onChange={e => setCostForm({ ...costForm, user_id: e.target.value })} style={inputStyle} /></div>
              <div><label style={labelStyle}>Hourly Rate ($) *</label><input type="number" step="0.01" value={costForm.hourly_rate} onChange={e => setCostForm({ ...costForm, hourly_rate: e.target.value })} style={inputStyle} /></div>
              <div><label style={labelStyle}>Effective From *</label><input type="date" value={costForm.effective_from} onChange={e => setCostForm({ ...costForm, effective_from: e.target.value })} style={inputStyle} /></div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
              <button onClick={() => setShowCostModal(false)} style={cancelBtn}>Cancel</button>
              <button onClick={saveCostRate} style={primaryBtn}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const pageStyle = { padding: '24px', maxWidth: '1400px', margin: '0 auto' };
const cardStyle = { backgroundColor: '#fff', borderRadius: '8px', padding: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0' };
const statCard = { backgroundColor: '#fff', borderRadius: '8px', padding: '12px 16px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0' };
const statLabel = { fontSize: '0.75rem', color: '#64748b' };
const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' };
const thStyle = { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #e2e8f0', fontSize: '0.75rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' };
const tdStyle = { padding: '8px 10px', borderBottom: '1px solid #f1f5f9', fontSize: '0.85rem' };
const badge = { display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontSize: '0.68rem', color: '#fff', fontWeight: 600, textTransform: 'capitalize' };
const inputStyle = { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem', width: '100%', boxSizing: 'border-box' };
const labelStyle = { fontSize: '0.75rem', fontWeight: 600, color: '#64748b', display: 'block', marginBottom: '4px' };
const primaryBtn = { padding: '8px 16px', backgroundColor: '#3b82f6', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' };
const cancelBtn = { padding: '8px 16px', backgroundColor: '#f1f5f9', color: '#334155', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem' };
const smallBtn = { padding: '4px 8px', backgroundColor: '#f1f5f9', color: '#334155', border: '1px solid #e2e8f0', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem' };
const actionBtn = { padding: '6px 12px', backgroundColor: '#3b82f6', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 };
const linkBtn = { background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', fontSize: '0.85rem', padding: 0 };
const overlayStyle = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const modalStyle = { backgroundColor: '#fff', borderRadius: '12px', padding: '24px', width: '90%', maxWidth: '500px', maxHeight: '85vh', overflowY: 'auto' };
